/* eslint-disable import/no-cycle */
import { Interpreter } from "xstate";
import { Subscription } from "rxjs";
import {
    MatterportService,
    ContentService,
    VisitTemplate,
    TagAction,
    waitUntil,
} from "@smarterplan/ngx-smarterplan-core";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { OutOfZoneAlertComponent } from "src/app/museum/components/modals/out-of-zone-alert/out-of-zone-alert.component";
import { MuseumTagService } from "src/app/museum/museum-tag.service";
import { Logger } from "aws-amplify";
import { PhotonClient } from "../PhotonClient";
import { FSMSchema, FSMEvent, makeSimulationFSM } from "./SimulationFSM";
import { CameraPose, Event, Frame, IVector3, ZoomData } from "../types";
import { LocalTime } from "../time/local-time";
import { RemoteControlService } from "../remote-control.service";
/**
 * Action Event codes:
 * 0 - "pose"
 * 1 - "sweepExit"
 * 2 - "tag hover"
 * 3 - "tag Event"
 */

const frameFromPose = (
    step: number,
    pose: CameraPose,
    zoom: ZoomData,
): Frame => {
    return {
        step,
        pose: {
            position: {
                x: pose.position.x,
                y: pose.position.y,
                z: pose.position.z,
            },
            rotation: {
                x: pose.rotation.x,
                y: pose.rotation.y,
            },
            mode: pose.mode,
            sweep: pose.sweep,
        },
        zoom: zoom.level,
    };
};

export class LocalSimulator {
    private fsm: Interpreter<unknown, FSMSchema, FSMEvent, any, any> = null;

    private sdk: any = null;

    private cameraPose: CameraPose = null;

    private cameraPoseSub: any = null;

    private zoomSub: any = null;

    private zoom: ZoomData = { level: 1 };

    private time: LocalTime;

    private tmp: IVector3 = { x: 0, y: 0, z: 0 };

    private elementIdHovered: string = null;

    private tagSub: Subscription;

    private closeModalSub: Subscription;

    private nextSlideSub: Subscription;

    private goToTagSub: Subscription;

    private alertZoneChecked = false;

    logger: Logger;

    loggerInfo: { name: string; tourID: string; };

    lastFrame: Frame = null;

    constructor(
        private photonClient: PhotonClient,
        sdkInject: any,
        private matterportService: MatterportService,
        private contentService: ContentService,
        private liveTour: VisitTemplate,
        private modalService: NgbModal,
        private tagService: MuseumTagService,
        private remoteService: RemoteControlService,
    ) {
        this.sdk = sdkInject;
        this.sendSweepExit = this.sendSweepExit.bind(this);
        this.sendHoverOn = this.sendHoverOn.bind(this);

        this.fsm = makeSimulationFSM(
            this.onEnterIdle,
            this.onEnterInitialized,
            this.onEnterSimulating,
            this.onExitSimulating,
        );
        this.fsm.start();
        this.time = new LocalTime(photonClient);
    }

    /**
     * Set Logger
     */
    setLogger(logger: Logger, loggerInfo: { name: string; tourID: string; }) {
        this.logger = logger;
        this.loggerInfo = loggerInfo;
    }

    public async start() {
        // this.iframe = iframe;
        this.fsm.send("START");
    }

    public stop() {
        this.fsm.send("STOP");
    }

    private onEnterIdle = async () => {
        // this.iframe = null;
        // this.sdk = null;
    };

    private onEnterInitialized = async () => {
        this.fsm.send("SDK_READY");
    };

    private onEnterSimulating = async () => {
        this.time.start();
        this.cameraPoseSub = this.sdk.Camera.pose.subscribe(
            (pose: CameraPose) => {
                this.cameraPose = pose;
                this.sendCameraPose();
            },
        );
        this.zoomSub = this.sdk.Camera.zoom.subscribe((zoom: ZoomData) => {
            this.zoom = zoom;
            this.sendCameraPose();
        });
        this.sdk.on(this.sdk.Sweep.Event.EXIT, this.sendSweepExit);
        this.sdk.on(this.sdk.Mattertag.Event.HOVER, this.sendHoverOn);
        this.goToTagSub = this.matterportService.onGoToTag.subscribe(
            (tagID) => {
                this.sendHoverOn(tagID, true);
            },
        );
        this.tagSub = this.tagService.tagAction.subscribe((load) => {
            this.sendTagAction(load);
        });
        this.closeModalSub = this.contentService.closeModal.subscribe(() => {
            console.log("close modal");
            this.sendCloseModal();
        });
        this.nextSlideSub = this.contentService.nextSlide.subscribe(
            (slideID) => {
                this.sendNextSlide(slideID);
            },
        );
        // send first update
        await waitUntil(() => this.cameraPose !== undefined);
        this.sendCameraPose();
    };

    private onExitSimulating = async () => {
        this.lastFrame = null;
        this.cameraPoseSub.cancel();
        this.cameraPoseSub = null;
        this.zoomSub.cancel();
        this.zoomSub = null;
        this.sdk.off(this.sdk.Sweep.Event.EXIT, this.sendSweepExit);
        this.sdk.off(this.sdk.Mattertag.Event.HOVER, this.sendHoverOn);
        this.tagSub.unsubscribe();
        this.closeModalSub.unsubscribe();
        this.nextSlideSub.unsubscribe();
        this.goToTagSub.unsubscribe();
        this.remoteService.currentTagOnlineChange.next(null);
        this.time.stop();
    };

    private sendSweepExit(oldSweep, newSweep) {
        const currentStep = this.time.currentStep();
        const event: Event = {
            type: "sweep.exit",
            step: currentStep,
            args: [oldSweep, newSweep],
        };
        console.log("sweep exit, step", currentStep);
        if (
            !this.liveTour.zone.sweepIDs.includes(newSweep) &&
            !this.alertZoneChecked
        ) {
            const modalReference = this.modalService.open(
                OutOfZoneAlertComponent,
                {
                    modalDialogClass: "museum-modal",
                },
            );
            modalReference.componentInstance.onCheck.subscribe((checked) => {
                if (checked) {
                    this.alertZoneChecked = true;
                }
            });
        }
        this.photonClient.raiseEvent(1, event, {
            cache: Photon.LoadBalancing.Constants.EventCaching.ReplaceCache,
        });
    }

    private sendHoverOn(tagSid, hovering) {
        const currentStep = this.time.currentStep();
        if (hovering) {
            this.setElementFromForSid(tagSid);
            this.remoteService.currentTagOnlineChange.next(
                this.elementIdHovered,
            );
        } else {
            this.elementIdHovered = null;
        }
        const event: Event = {
            type: "tag.hover",
            step: currentStep,
            args: [this.elementIdHovered],
        };
        if (hovering) {
            console.log(
                "tag hover raise event for elementID",
                this.elementIdHovered,
                "step",
                currentStep,
            );
            this.photonClient.raiseEvent(2, event, {
                cache: Photon.LoadBalancing.Constants.EventCaching.ReplaceCache,
            });
            // and we clean all other "moving" events
            this.photonClient.raiseEvent(
                0,
                {},
                {
                    cache: Photon.LoadBalancing.Constants.EventCaching
                        .RemoveCache,
                },
            );
            this.photonClient.raiseEvent(
                1,
                {},
                {
                    cache: Photon.LoadBalancing.Constants.EventCaching
                        .RemoveCache,
                },
            );
        } else {
            this.photonClient.raiseEvent(
                2,
                {},
                {
                    cache: Photon.LoadBalancing.Constants.EventCaching
                        .RemoveCache,
                },
            );
        }
    }

    private sendCameraPose() {
        if (this.lastFrame) {
            const rotChanged =
                Math.abs(
                    this.cameraPose.rotation.x - this.lastFrame.pose.rotation.x,
                ) > 0.02 ||
                Math.abs(
                    this.cameraPose.rotation.y - this.lastFrame.pose.rotation.y,
                ) > 0.03;
            this.tmp.x =
                this.cameraPose.position.x - this.lastFrame.pose.position.x;
            this.tmp.y =
                this.cameraPose.position.y - this.lastFrame.pose.position.y;
            this.tmp.z =
                this.cameraPose.position.z - this.lastFrame.pose.position.z;
            const posChanged =
                this.tmp.x * this.tmp.x +
                this.tmp.y * this.tmp.y +
                this.tmp.z * this.tmp.z >
                0.001;
            const zoomChanged = this.zoom.level !== this.lastFrame.zoom;
            // only update the state if the pose has changed.
            if (posChanged || rotChanged || zoomChanged) {
                const currentStep = this.time.currentStep();
                this.lastFrame = frameFromPose(
                    currentStep,
                    this.cameraPose,
                    this.zoom,
                );
                const event: Event = {
                    type: "pose",
                    step: currentStep,
                    args: [this.lastFrame],
                };
                this.photonClient.raiseEvent(0, event, {
                    cache: Photon.LoadBalancing.Constants.EventCaching
                        .ReplaceCache,
                });
            }
        } else {
            const currentStep = this.time.currentStep();
            this.lastFrame = frameFromPose(
                currentStep,
                this.cameraPose,
                this.zoom,
            );
            const event: Event = {
                type: "pose",
                step: currentStep,
                args: [this.lastFrame],
            };
            this.photonClient.raiseEvent(0, event, {
                cache: Photon.LoadBalancing.Constants.EventCaching.ReplaceCache,
            });
            // for first sweep
            const sweepEvent = {
                type: "sweep.exit",
                step: currentStep,
                args: [0, this.cameraPose.sweep],
            };
            this.photonClient.raiseEvent(1, sweepEvent, {
                cache: Photon.LoadBalancing.Constants.EventCaching.ReplaceCache,
            });
        }
    }

    private sendTagAction(load: { action: string; data: string; }) {
        const currentStep = this.time.currentStep();
        const event: Event = {
            type: "tagAction",
            step: currentStep,
            args: [load.action, load.data],
        };
        this.logger.info(
            `${JSON.stringify(this.loggerInfo)}: HOST tag action ${load.action
            } with data ${load.data} on step ${currentStep}`,
        );
        this.photonClient.raiseEvent(3, event, {
            cache: Photon.LoadBalancing.Constants.EventCaching.ReplaceCache,
        });
    }

    private setElementFromForSid(sid: string) {
        const data = this.matterportService.getMatterTagDataForMattertag(sid);
        this.elementIdHovered = data ? data.elementID : null;
    }

    private sendCloseModal() {
        const currentStep = this.time.currentStep();
        const event: Event = {
            type: "tagAction",
            step: currentStep,
            args: [TagAction.CLOSE_MODAL, 1],
        };
        console.log("close modal, step", currentStep);
        this.photonClient.raiseEvent(0, event);
        // cancel all tag events (event code 3)
        this.photonClient.raiseEvent(
            3,
            {},
            {
                cache: Photon.LoadBalancing.Constants.EventCaching.RemoveCache,
            },
        );
    }

    private sendNextSlide(slideID) {
        const currentStep = this.time.currentStep();
        const event: Event = {
            type: "tagAction",
            step: currentStep,
            args: [TagAction.NEXT_SLIDE, slideID],
        };
        console.log("next slide, step", currentStep);
        this.photonClient.raiseEvent(0, event);
    }
}
